/**
 * Shopro 第三方平台功能聚合
 * @version 1.0.3
 * @author lidongtony
 */

// @ts-ignore
// #ifdef H5
import { isWxBrowser } from '@/sheep/helper/utils';
// #endif

import wechat from './provider/wechat/index';
import apple from './provider/apple';

import Pay from './pay';

// ===== 惰性初始化：使用 getter 延迟获取，避免模块加载时阻塞 =====

/** 设备信息 - 惰性获取 */
let _device: any = null;
const getDevice = () => {
  if (!_device) {
    _device = uni.getWindowInfo();
  }
  return _device;
};

/** 系统平台 - 惰性获取 */
let _os: string | null = null;
const getOs = (): string => {
  if (_os === null) {
    _os = uni.getDeviceInfo().platform || '';
  }
  return _os;
};

/** 平台名称 - 惰性计算 */
let _name: string | null = null;
const getPlatformName = (): string => {
  if (_name !== null) return _name;

  // #ifdef H5
  if (isWxBrowser()) {
    _name = 'WechatOfficialAccount';
  } else {
    _name = 'H5';
  }
  // #endif

  // #ifdef APP-PLUS
  _name = 'App';
  // #endif

  // #ifdef MP-WEIXIN
  _name = 'WechatMiniProgram';
  // #endif

  if (!_name) {
    _name = 'Unknown';
    // 使用 setTimeout 避免在模块初始化时立即显示 toast
    setTimeout(() => {
      uni.showToast({
        title: '暂不支持该平台',
        icon: 'none',
      });
    }, 0);
  }

  return _name;
};

/** 厂商 - 惰性计算 */
let _provider: string | null = null;
const getProvider = (): string => {
  if (_provider !== null) return _provider;

  // #ifdef H5
  if (isWxBrowser()) {
    _provider = 'wechat';
  }
  // #endif

  // #ifdef MP-WEIXIN
  _provider = 'wechat';
  // #endif

  if (!_provider) {
    _provider = '';
  }
  return _provider;
};

/** 平台标识 - 惰性计算 */
let _platformId: string | null = null;
const getPlatform = (): string => {
  if (_platformId !== null) return _platformId;

  // #ifdef H5
  if (isWxBrowser()) {
    _platformId = 'officialAccount';
  } else {
    _platformId = 'h5';
  }
  // #endif

  // #ifdef APP-PLUS
  _platformId = 'openPlatform';
  // #endif

  // #ifdef MP-WEIXIN
  _platformId = 'miniProgram';
  // #endif

  if (!_platformId) {
    _platformId = '';
  }
  return _platformId;
};

/** 是否安装微信 - 惰性获取 */
let _isWechatInstalled: boolean | null = null;
const getIsWechatInstalled = (): boolean => {
  if (_isWechatInstalled !== null) return _isWechatInstalled;

  // #ifdef APP-PLUS
  const os = getOs();
  if (os === 'ios') {
    try {
      _isWechatInstalled = plus.ios.import('WXApi').isWXAppInstalled();
    } catch (e) {
      console.warn('检查微信安装状态失败:', e);
      _isWechatInstalled = true; // 默认假设已安装
    }
  } else {
    _isWechatInstalled = true;
  }
  // #endif

  // #ifndef APP-PLUS
  _isWechatInstalled = true;
  // #endif

  return _isWechatInstalled;
};

/** 加载当前平台前置行为 */
const load = () => {
  const provider = getProvider();
  if (provider === 'wechat') {
    wechat.load();
  }
};

/**
 * 使用厂商独占sdk
 * @param _provider 厂商名称: 'wechat' | 'alipay' | 'apple'
 */
const useProvider = (_provider = '') => {
  if (_provider === '') _provider = getProvider();
  if (_provider === 'wechat') return wechat;
  if (_provider === 'apple') return apple;
  return null;
};

/**
 * 支付服务转发
 * @param payment 支付方式
 * @param orderType 订单类型
 * @param orderSN 订单号
 */
const pay = (payment: string, orderType: string, orderSN: string) => {
  return new Pay(payment, orderType, orderSN);
};

/**
 * 检查更新 (只检查小程序和App)
 * @param silence 静默检查
 */
const checkUpdate = (silence = false) => {
  // #ifdef MP-WEIXIN
  useProvider()?.checkUpdate(silence);
  // #endif

  // #ifdef APP-PLUS
  // TODO: 热更新
  // #endif
};

/**
 * 检查网络
 * @returns 是否有网络
 */
async function checkNetwork(): Promise<boolean> {
  const networkStatus = await uni.getNetworkType();
  if (networkStatus.networkType === 'none') {
    return Promise.resolve(false);
  }
  return Promise.resolve(true);
}

/** 胶囊按钮信息 */
const getCapsule = () => {
  // #ifdef MP
  let capsule = uni.getMenuButtonBoundingClientRect();
  if (!capsule) {
    capsule = {
      bottom: 56,
      height: 32,
      left: 278,
      right: 365,
      top: 24,
      width: 87,
    };
  }
  return capsule;
  // #endif

  // #ifndef MP
  return {
    bottom: 56,
    height: 32,
    left: 278,
    right: 365,
    top: 24,
    width: 87,
  };
  // #endif
};

/** 标题栏高度 */
const getNavBar = () => {
  return getDevice().statusBarHeight + 44;
};

/** 获取落地页 */
function getLandingPage(): string {
  let page = '';
  // #ifdef H5
  page = location.href.split('?')[0];
  // #endif
  return page;
}

/** 平台对象 - 使用 getter 实现惰性初始化 */
const _platform = {
  get name() { return getPlatformName(); },
  get device() { return getDevice(); },
  get os() { return getOs(); },
  get provider() { return getProvider(); },
  get platform() { return getPlatform(); },
  useProvider,
  checkUpdate,
  checkNetwork,
  pay,
  load,
  get capsule() { return getCapsule(); },
  get navbar() { return getNavBar(); },
  get landingPage() { return getLandingPage(); },
  get isWechatInstalled() { return getIsWechatInstalled(); },
};

export default _platform;
